import type { PIPELINE_STAGE_COLORS } from "@school-sales-ai/shared";

// Maps each palette color name (validated in shared) to Tailwind classes. Static
// strings so the JIT compiler picks them up. `swatch` is the solid dot shown in
// the color picker; `classes` styles the column badge/pill.
export const PIPELINE_COLOR_OPTIONS: {
  name: (typeof PIPELINE_STAGE_COLORS)[number];
  classes: string;
  swatch: string;
}[] = [
  { name: "slate", classes: "bg-slate-100 text-slate-700", swatch: "bg-slate-400" },
  { name: "blue", classes: "bg-blue-100 text-blue-700", swatch: "bg-blue-500" },
  { name: "teal", classes: "bg-teal-100 text-teal-700", swatch: "bg-teal-500" },
  { name: "green", classes: "bg-green-100 text-green-700", swatch: "bg-green-500" },
  { name: "amber", classes: "bg-amber-100 text-amber-800", swatch: "bg-amber-500" },
  { name: "orange", classes: "bg-orange-100 text-orange-700", swatch: "bg-orange-500" },
  { name: "rose", classes: "bg-rose-100 text-rose-700", swatch: "bg-rose-500" },
  { name: "purple", classes: "bg-purple-100 text-purple-700", swatch: "bg-purple-500" },
];

// Left-accent border class per palette color, used on Kanban cards to flag a lead.
// Literal strings so the Tailwind JIT compiler keeps them. Keys mirror the palette
// names in PIPELINE_COLOR_OPTIONS.
const LEAD_BORDER_CLASSES: Record<string, string> = {
  slate: "border-l-slate-400",
  blue: "border-l-blue-500",
  teal: "border-l-teal-500",
  green: "border-l-green-500",
  amber: "border-l-amber-500",
  orange: "border-l-orange-500",
  rose: "border-l-rose-500",
  purple: "border-l-purple-500",
};

// Resolves the left-border color class for a lead's color, or "" when unset/unknown.
export function leadBorderClass(color: string | null | undefined): string {
  if (!color) return "";
  return LEAD_BORDER_CLASSES[color] ?? "";
}

const NEUTRAL_CLASSES = "bg-ink-100 text-ink-600";

// Badge classes for a lead label chip: an explicit palette color wins; a label
// with no color falls back to a neutral chip. Reuses the shared palette map.
export function labelChipClasses(color: string | null | undefined): string {
  if (color) {
    const opt = PIPELINE_COLOR_OPTIONS.find((c) => c.name === color);
    if (opt) return opt.classes;
  }
  return NEUTRAL_CLASSES;
}

// Fallback badge classes for the 7 seeded keys (kept from the original design so
// the default columns keep their familiar colors when `color` is unset).
const KNOWN_STAGE_CLASSES: Record<string, string> = {
  new: "bg-stage-nuevo-bg text-stage-nuevo-fg",
  qualifying: "bg-stage-calificando-bg text-stage-calificando-fg",
  warm: "bg-stage-tibio-bg text-stage-tibio-fg",
  hot: "bg-stage-caliente-bg text-stage-caliente-fg",
  closing: "bg-stage-cerrando-bg text-stage-cerrando-fg",
  enrolled: "bg-stage-inscrito-bg text-stage-inscrito-fg",
  discarded: "bg-stage-descartado-bg text-stage-descartado-fg",
};

// Resolves badge classes for a column: an explicit palette color wins; otherwise
// fall back to the seeded key's classes; otherwise a neutral chip.
export function stageColorClasses(
  key: string,
  color: string | null | undefined,
): string {
  if (color) {
    const opt = PIPELINE_COLOR_OPTIONS.find((c) => c.name === color);
    if (opt) return opt.classes;
  }
  return KNOWN_STAGE_CLASSES[key] ?? NEUTRAL_CLASSES;
}
